
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Constants, GlobalSettings } from '../../../common';
import { IChangePasswordData, IResponseMessage } from '../../../models';
import { AuthService, CommonService, ToasterService } from '../../../services';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-change-password',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule
  ],
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.scss'
})
export class ChangePasswordComponent implements OnInit {

  limits = GlobalSettings.characterLimits;

  changePasswordForm!: FormGroup;

  hidePassword1 = true;
  hidePassword2 = true;
  hidePassword3 = true;

  isLoading = false;

  @Output() closeEvent = new EventEmitter<string>();

  constructor(private fb: FormBuilder, private authService: AuthService, private toaster: ToasterService, private commonService: CommonService) { }

  ngOnInit(): void {
    this.initializeForms();
  }

  initializeForms() {
    const changePasswordInfo: IChangePasswordData = {} as IChangePasswordData;
    const group: any = {
      new_password: [changePasswordInfo.new_password, [Validators.required, Validators.pattern(Constants.passwordPattern)]],
      confirm_password: [changePasswordInfo.confirm_password, [Validators.required, Validators.pattern(Constants.passwordPattern)]]
    };

    // Conditionally add old_password if user has a password
    if (this.commonService.profileDetail.has_password) {
      group.old_password = [changePasswordInfo.old_password, [Validators.required, Validators.pattern(Constants.passwordPattern)]];
    }

    this.changePasswordForm = this.fb.group(group, { validators: this.passwordMatchValidator });
  }

  getPasswordErrorMessage(controlName: string): string {
    const password = this.changePasswordForm.get(controlName)?.value || '';
    if (!password) {
      if (controlName === 'old_password') return 'Current Password is required';
      if (controlName === 'new_password') return 'New Password is required';
      return 'Confirm Password is required';
    }

    let errors = [];
    if (password.length < 8) errors.push('8 characters long');
    if (!/[A-Z]/.test(password)) errors.push('one uppercase letter');
    if (!/[a-z]/.test(password)) errors.push('one lowercase letter');
    if (!/[0-9]/.test(password)) errors.push('one number');
    if (!/[^A-Za-z0-9]/.test(password)) errors.push('one special character');

    if (errors.length === 0) return '';

    if (errors.length === 1) {
      if (password.length < 8) return 'Password must be at least 8 characters long.';
      return `Password must include at least ${errors[0]}.`;
    }

    if (password.length < 8) {
      errors.shift();
      if (errors.length === 1) {
        return `Password must be at least 8 characters long and include at least ${errors[0]}.`;
      }
      const last = errors.pop();
      return `Password must be at least 8 characters long and include at least ${errors.join(', ')}, and ${last}.`;
    } else {
      const last = errors.pop();
      return `Password must include at least ${errors.join(', ')}, and ${last}.`;
    }
  }

  onFormSubmit() {
    if (this.changePasswordForm.valid) {
      this.commonService.isLoading = true;
      this.isLoading = true;
      this.authService.changePassword(this.changePasswordForm.value).subscribe({
        next: (data: IResponseMessage) => {
          this.toaster.success(data?.message ? data.message : Constants.notifyMessages.passwordResetSuccess);
          // console.log(data);
          this.isLoading = false;
          this.commonService.isLoading = false;
          this.commonService.profileDetail.has_password = true;
          this.cancelClick();
        },
        error: (error: HttpErrorResponse) => {
          this.isLoading = false;
          this.commonService.isLoading = false;
          this.toaster.error(error?.error?.message ? error?.error.message : Constants.notifyMessages.registerFailed);
          console.log(error);
        }
      })
    }
    else {
      Object.values(this.changePasswordForm.controls).forEach(control => {
        control.markAsTouched();
      });
    }
  }

  getFormHeight() {
    return window.innerHeight - 280;
  }

  cancelClick() {
    this.closeEvent.emit('close');
  }

  hasPassword() {
    return this.commonService.profileDetail.has_password;
  }

  passwordMatchValidator(formGroup: AbstractControl): ValidationErrors | null {
    const newPassword = formGroup.get('new_password')?.value;
    const confirmPassword = formGroup.get('confirm_password')?.value;
    if (newPassword !== confirmPassword) {
      formGroup.get('confirm_password')?.setErrors({ mismatch: true });
      return { mismatch: true };
    } else {
      // Only clear mismatch error, not other errors
      const errors = formGroup.get('confirm_password')?.errors;
      if (errors) {
        delete errors['mismatch'];
        if (Object.keys(errors).length === 0) {
          formGroup.get('confirm_password')?.setErrors(null);
        } else {
          formGroup.get('confirm_password')?.setErrors(errors);
        }
      }
      return null;
    }
  }

}
